import React, { useState, useEffect } from 'react';
import { Publisher, SourceType, MetadataType, getAllAuthors, addAuthor, removeAuthor, getAllPublishers, addPublisher, removePublisher, getAllSourceTypes, createSourceType, updateSourceType, deleteSourceType, getAllMetadataTypes, createMetadataType, updateMetadataType, deleteMetadataType } from '../services/adminApi';
import ConfirmDialog from '../components/ConfirmDialog';
import './Admin.css';

const Admin: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'authors' | 'publishers' | 'sourceTypes' | 'metadataTypes'>('authors');
    const [authors, setAuthors] = useState<string[]>([]);
    const [publishers, setPublishers] = useState<Publisher[]>([]);
    const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
    const [metadataTypes, setMetadataTypes] = useState<MetadataType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Form states
    const [showAuthorForm, setShowAuthorForm] = useState(false);
    const [editingAuthorName, setEditingAuthorName] = useState<string | null>(null);
    const [authorName, setAuthorName] = useState('');

    const [showPublisherForm, setShowPublisherForm] = useState(false);
    const [editingPublisherName, setEditingPublisherName] = useState<string | null>(null);
    const [publisherName, setPublisherName] = useState('');
    const [publisherShortName, setPublisherShortName] = useState('');

    const [showSourceTypeForm, setShowSourceTypeForm] = useState(false);
    const [editingSourceType, setEditingSourceType] = useState<SourceType | null>(null);
    const [sourceTypeType, setSourceTypeType] = useState<string>('');
    const [compulsoryFields, setCompulsoryFields] = useState<string[]>([]);
    const [suggestedFields, setSuggestedFields] = useState<string[]>([]);

    const [showMetadataTypeForm, setShowMetadataTypeForm] = useState(false);
    const [editingMetadataType, setEditingMetadataType] = useState<MetadataType | null>(null);
    const [metadataTypeFieldName, setMetadataTypeFieldName] = useState('');
    const [metadataTypeFieldType, setMetadataTypeFieldType] = useState<MetadataType['field_type']>('text');
    const [metadataTypeEnumOptions, setMetadataTypeEnumOptions] = useState<string>('');
    
    // Checklist editing state
    const [checklistItems, setChecklistItems] = useState<Array<{id: string, text?: string, keyword?: string, action?: string, checked: boolean}>>([]);

    // Confirm dialog states
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    const [confirmMessage, setConfirmMessage] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [authorsData, publishersData, sourceTypesData, metadataTypesData] = await Promise.all([
                getAllAuthors(),
                getAllPublishers(),
                getAllSourceTypes(),
                getAllMetadataTypes()
            ]);
            
            setAuthors(authorsData);
            setPublishers(publishersData);
            setSourceTypes(sourceTypesData);
            setMetadataTypes(metadataTypesData);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Author management functions
    const handleAddAuthor = () => {
        setEditingAuthorName(null);
        setAuthorName('');
        setShowAuthorForm(true);
    };

    const handleEditAuthor = (authorName: string) => {
        setEditingAuthorName(authorName);
        setAuthorName(authorName);
        setShowAuthorForm(true);
    };

    const handleSaveAuthor = async () => {
        try {
            if (editingAuthorName) {
                // Remove old name and add new one
                await removeAuthor(editingAuthorName);
                await addAuthor(authorName);
                setAuthors(authors.filter(a => a !== editingAuthorName).concat([authorName]));
            } else {
                await addAuthor(authorName);
                setAuthors([...authors, authorName]);
            }
            setShowAuthorForm(false);
            setEditingAuthorName(null);
            setAuthorName('');
            await loadData(); // Reload to ensure sync
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save author');
        }
    };

    const handleDeleteAuthor = (authorName: string) => {
        setConfirmMessage(`Are you sure you want to delete the author "${authorName}"?`);
        setConfirmAction(() => async () => {
            try {
                await removeAuthor(authorName);
                setAuthors(authors.filter(a => a !== authorName));
                setShowConfirmDialog(false);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to delete author');
            }
        });
        setShowConfirmDialog(true);
    };

    // Publisher management functions
    const handleAddPublisher = () => {
        setEditingPublisherName(null);
        setPublisherName('');
        setPublisherShortName('');
        setShowPublisherForm(true);
    };

    const handleEditPublisher = (publisher: Publisher) => {
        setEditingPublisherName(publisher.name);
        setPublisherName(publisher.name);
        setPublisherShortName(publisher.shortName || '');
        setShowPublisherForm(true);
    };

    const handleSavePublisher = async () => {
        try {
            if (editingPublisherName) {
                // Remove old publisher and add new one
                await removePublisher(editingPublisherName);
                await addPublisher(publisherName, publisherShortName || undefined);
            } else {
                await addPublisher(publisherName, publisherShortName || undefined);
            }
            setShowPublisherForm(false);
            setEditingPublisherName(null);
            setPublisherName('');
            setPublisherShortName('');
            await loadData(); // Reload to ensure sync
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save publisher');
        }
    };

    const handleDeletePublisher = (publisher: Publisher) => {
        setConfirmMessage(`Are you sure you want to delete the publisher "${publisher.name}"?`);
        setConfirmAction(() => async () => {
            try {
                await removePublisher(publisher.name);
                setPublishers(publishers.filter(p => p.name !== publisher.name));
                setShowConfirmDialog(false);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to delete publisher');
            }
        });
        setShowConfirmDialog(true);
    };

    // Source Type management functions
    const handleAddSourceType = () => {
        setEditingSourceType(null);
        setSourceTypeType('');
        setCompulsoryFields([]);
        setSuggestedFields([]);
        setShowSourceTypeForm(true);
    };

    const handleEditSourceType = (sourceType: SourceType) => {
        setEditingSourceType(sourceType);
        setSourceTypeType(sourceType.source_type);
        setCompulsoryFields([...sourceType.compulsory]);
        setSuggestedFields([...sourceType.suggested]);
        setShowSourceTypeForm(true);
    };

    const handleSaveSourceType = async () => {
        try {
            if (!sourceTypeType.trim()) {
                setError('Source type name is required');
                return;
            }
            
            if (editingSourceType) {
                const updated = await updateSourceType(editingSourceType.id, compulsoryFields, suggestedFields);
                setSourceTypes(sourceTypes.map(st => st.id === editingSourceType.id ? updated : st));
            } else {
                const newSourceType = await createSourceType(sourceTypeType.trim(), compulsoryFields, suggestedFields);
                setSourceTypes([...sourceTypes, newSourceType]);
            }
            setShowSourceTypeForm(false);
            setEditingSourceType(null);
            setSourceTypeType('');
            setCompulsoryFields([]);
            setSuggestedFields([]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save source type');
        }
    };

    const handleDeleteSourceType = (sourceType: SourceType) => {
        setConfirmMessage(`Are you sure you want to delete the source type "${sourceType.source_type}"?`);
        setConfirmAction(() => async () => {
            try {
                await deleteSourceType(sourceType.id);
                setSourceTypes(sourceTypes.filter(st => st.id !== sourceType.id));
                setShowConfirmDialog(false);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to delete source type');
            }
        });
        setShowConfirmDialog(true);
    };

    // Metadata Type management functions
    const handleAddMetadataType = () => {
        setEditingMetadataType(null);
        setMetadataTypeFieldName('');
        setMetadataTypeFieldType('text');
        setMetadataTypeEnumOptions('');
        setShowMetadataTypeForm(true);
    };

    const handleEditMetadataType = (metadataType: MetadataType) => {
        setEditingMetadataType(metadataType);
        setMetadataTypeFieldName(metadataType.field_name);
        setMetadataTypeFieldType(metadataType.field_type);
        
        // Check if this is a checklist entry
        const isChecklist = metadataType.field_name === 'process_metadata_editor_checklist' || 
                           metadataType.field_name === 'process_metadata_review_checklist';
        
        if (isChecklist && metadataType.enum_options && Array.isArray(metadataType.enum_options)) {
            // Parse checklist items from enum_options
            // enum_options can be string[] or checklist item objects, so we need to check and cast
            // First cast to unknown, then to the checklist items type
            const items = (metadataType.enum_options as unknown) as Array<{id: string, text?: string, keyword?: string, action?: string, checked: boolean}>;
            setChecklistItems(items);
            setMetadataTypeEnumOptions('');
        } else {
            setMetadataTypeEnumOptions(metadataType.enum_options ? metadataType.enum_options.join(', ') : '');
            setChecklistItems([]);
        }
        
        setShowMetadataTypeForm(true);
    };

    const handleSaveMetadataType = async () => {
        try {
            if (!metadataTypeFieldName.trim()) {
                setError('Field name is required');
                return;
            }

            // Check if this is a checklist entry
            const isChecklist = metadataTypeFieldName === 'process_metadata_editor_checklist' || 
                               metadataTypeFieldName === 'process_metadata_review_checklist';
            
            let enumOptionsArray: any = null;
            
            if (isChecklist) {
                // Use checklist items as enum_options
                enumOptionsArray = checklistItems.length > 0 ? checklistItems : null;
            } else {
                // Use regular enum options
                enumOptionsArray = metadataTypeEnumOptions.trim() 
                    ? metadataTypeEnumOptions.split(',').map(opt => opt.trim()).filter(opt => opt)
                    : null;
            }

            if (editingMetadataType) {
                const updated = await updateMetadataType(
                    editingMetadataType.id,
                    metadataTypeFieldName.trim(),
                    metadataTypeFieldType,
                    enumOptionsArray
                );
                setMetadataTypes(metadataTypes.map(mt => mt.id === editingMetadataType.id ? updated : mt));
            } else {
                const newMetadataType = await createMetadataType(
                    metadataTypeFieldName.trim(),
                    metadataTypeFieldType,
                    enumOptionsArray
                );
                setMetadataTypes([...metadataTypes, newMetadataType]);
            }
            setShowMetadataTypeForm(false);
            setEditingMetadataType(null);
            setMetadataTypeFieldName('');
            setMetadataTypeFieldType('text');
            setMetadataTypeEnumOptions('');
            setChecklistItems([]);
            await loadData(); // Reload to ensure sync
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save metadata type');
        }
    };

    const handleDeleteMetadataType = (metadataType: MetadataType) => {
        setConfirmMessage(`Are you sure you want to delete the metadata type "${metadataType.field_name}"?`);
        setConfirmAction(() => async () => {
            try {
                await deleteMetadataType(metadataType.id);
                setMetadataTypes(metadataTypes.filter(mt => mt.id !== metadataType.id));
                setShowConfirmDialog(false);
                await loadData(); // Reload to ensure sync
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to delete metadata type');
            }
        });
        setShowConfirmDialog(true);
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>Admin Panel</h1>
                <p>Manage authors, publishers, and source types</p>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            <div className="admin-tabs">
                <button 
                    className={`tab-button ${activeTab === 'authors' ? 'active' : ''}`}
                    onClick={() => setActiveTab('authors')}
                >
                    Authors ({authors.length})
                </button>
                <button 
                    className={`tab-button ${activeTab === 'publishers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('publishers')}
                >
                    Publishers ({publishers.length})
                </button>
                <button 
                    className={`tab-button ${activeTab === 'sourceTypes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sourceTypes')}
                >
                    Source Types ({sourceTypes.length})
                </button>
                <button 
                    className={`tab-button ${activeTab === 'metadataTypes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('metadataTypes')}
                >
                    Metadata Types ({metadataTypes.length})
                </button>
            </div>

            <div className="tab-content">
                {/* Authors Tab */}
                {activeTab === 'authors' && (
                    <div className="authors-section">
                        <div className="section-header">
                            <h2>Authors</h2>
                            <button className="add-button" onClick={handleAddAuthor}>
                                Add Author
                            </button>
                        </div>

                        {showAuthorForm && (
                            <div className="form-overlay">
                                <div className="form-modal">
                                    <h3>{editingAuthorName ? 'Edit Author' : 'Add Author'}</h3>
                                    <div className="form-group">
                                        <label>Name:</label>
                                        <input
                                            type="text"
                                            value={authorName}
                                            onChange={(e) => setAuthorName(e.target.value)}
                                            placeholder="Enter author name"
                                        />
                                    </div>
                                    <div className="form-actions">
                                        <button onClick={handleSaveAuthor} disabled={!authorName.trim()}>
                                            {editingAuthorName ? 'Update' : 'Add'}
                                        </button>
                                        <button onClick={() => {
                                            setShowAuthorForm(false);
                                            setEditingAuthorName(null);
                                            setAuthorName('');
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="items-list">
                            {authors.length === 0 ? (
                                <p className="no-items">No authors found. Add your first author!</p>
                            ) : (
                                authors.map((author, index) => (
                                    <div key={index} className="item-card">
                                        <div className="item-info">
                                            <h4>{author}</h4>
                                        </div>
                                        <div className="item-actions">
                                            <button onClick={() => handleEditAuthor(author)}>
                                                Edit
                                            </button>
                                            <button 
                                                className="delete-button"
                                                onClick={() => handleDeleteAuthor(author)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Publishers Tab */}
                {activeTab === 'publishers' && (
                    <div className="publishers-section">
                        <div className="section-header">
                            <h2>Publishers</h2>
                            <button className="add-button" onClick={handleAddPublisher}>
                                Add Publisher
                            </button>
                        </div>

                        {showPublisherForm && (
                            <div className="form-overlay">
                                <div className="form-modal">
                                    <h3>{editingPublisherName ? 'Edit Publisher' : 'Add Publisher'}</h3>
                                    <div className="form-group">
                                        <label>Name:</label>
                                        <input
                                            type="text"
                                            value={publisherName}
                                            onChange={(e) => setPublisherName(e.target.value)}
                                            placeholder="Enter publisher name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Short Name (optional):</label>
                                        <input
                                            type="text"
                                            value={publisherShortName}
                                            onChange={(e) => setPublisherShortName(e.target.value)}
                                            placeholder="Enter short name"
                                        />
                                    </div>
                                    <div className="form-actions">
                                        <button onClick={handleSavePublisher} disabled={!publisherName.trim()}>
                                            {editingPublisherName ? 'Update' : 'Add'}
                                        </button>
                                        <button onClick={() => {
                                            setShowPublisherForm(false);
                                            setEditingPublisherName(null);
                                            setPublisherName('');
                                            setPublisherShortName('');
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="items-list">
                            {publishers.length === 0 ? (
                                <p className="no-items">No publishers found. Add your first publisher!</p>
                            ) : (
                                publishers.map((publisher, index) => (
                                    <div key={index} className="item-card">
                                        <div className="item-info">
                                            <h4>{publisher.name}</h4>
                                            {publisher.shortName && (
                                                <p className="short-name">({publisher.shortName})</p>
                                            )}
                                        </div>
                                        <div className="item-actions">
                                            <button onClick={() => handleEditPublisher(publisher)}>
                                                Edit
                                            </button>
                                            <button 
                                                className="delete-button"
                                                onClick={() => handleDeletePublisher(publisher)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Source Types Tab */}
                {activeTab === 'sourceTypes' && (
                    <div className="source-types-section">
                        <div className="section-header">
                            <h2>Source Types</h2>
                            <button className="add-button" onClick={handleAddSourceType}>
                                Add Source Type
                            </button>
                        </div>

                        {showSourceTypeForm && (
                            <div className="form-overlay">
                                <div className="form-modal">
                                    <h3>{editingSourceType ? 'Edit Source Type' : 'Add Source Type'}</h3>
                                    <div className="form-group">
                                        <label>Source Type:</label>
                                        {editingSourceType ? (
                                            <input
                                                type="text"
                                                value={sourceTypeType}
                                                disabled={true}
                                                className="form-input-disabled"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={sourceTypeType}
                                                onChange={(e) => setSourceTypeType(e.target.value)}
                                                placeholder="Source type..."
                                            />
                                        )}
                                    </div>
                                    <div className="form-group-row">
                                        <div className="form-group">
                                            <label>Compulsory Fields:</label>
                                            <select
                                                multiple
                                                size={6}
                                                value={compulsoryFields}
                                                onChange={(e) => {
                                                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                    setCompulsoryFields(selected);
                                                }}
                                                className="form-select-multiple"
                                            >
                                                {metadataTypes.map(mt => (
                                                    <option 
                                                        key={mt.id} 
                                                        value={mt.field_name}
                                                        disabled={suggestedFields.includes(mt.field_name)}
                                                    >
                                                        {mt.field_name} ({mt.field_type})
                                                    </option>
                                                ))}
                                            </select>
                                            <small className="form-help-text">
                                                Hold Ctrl/Cmd to select multiple. Selected: {compulsoryFields.length}
                                            </small>
                                            {compulsoryFields.length > 0 && (
                                                <div className="field-order-controls">
                                                    <label className="order-label">Reorder:</label>
                                                    <div className="orderable-field-list">
                                                        {compulsoryFields.map((fieldName, index) => {
                                                            const metadataType = metadataTypes.find(mt => mt.field_name === fieldName);
                                                            return (
                                                                <div key={fieldName} className="orderable-field-item">
                                                                    <div className="field-order-buttons">
                                                                        <button
                                                                            type="button"
                                                                            className="field-order-btn"
                                                                            onClick={() => {
                                                                                if (index > 0) {
                                                                                    const newFields = [...compulsoryFields];
                                                                                    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
                                                                                    setCompulsoryFields(newFields);
                                                                                }
                                                                            }}
                                                                            disabled={index === 0}
                                                                            title="Move up"
                                                                        >
                                                                            ↑
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="field-order-btn"
                                                                            onClick={() => {
                                                                                if (index < compulsoryFields.length - 1) {
                                                                                    const newFields = [...compulsoryFields];
                                                                                    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                                                                                    setCompulsoryFields(newFields);
                                                                                }
                                                                            }}
                                                                            disabled={index === compulsoryFields.length - 1}
                                                                            title="Move down"
                                                                        >
                                                                            ↓
                                                                        </button>
                                                                    </div>
                                                                    <span className="field-order-name">
                                                                        {fieldName} {metadataType && `(${metadataType.field_type})`}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Suggested Fields:</label>
                                            <select
                                                multiple
                                                size={6}
                                                value={suggestedFields}
                                                onChange={(e) => {
                                                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                    setSuggestedFields(selected);
                                                }}
                                                className="form-select-multiple"
                                            >
                                                {metadataTypes.map(mt => (
                                                    <option 
                                                        key={mt.id} 
                                                        value={mt.field_name}
                                                        disabled={compulsoryFields.includes(mt.field_name)}
                                                    >
                                                        {mt.field_name} ({mt.field_type})
                                                    </option>
                                                ))}
                                            </select>
                                            <small className="form-help-text">
                                                Hold Ctrl/Cmd to select multiple. Selected: {suggestedFields.length}
                                            </small>
                                            {suggestedFields.length > 0 && (
                                                <div className="field-order-controls">
                                                    <label className="order-label">Reorder:</label>
                                                    <div className="orderable-field-list">
                                                        {suggestedFields.map((fieldName, index) => {
                                                            const metadataType = metadataTypes.find(mt => mt.field_name === fieldName);
                                                            return (
                                                                <div key={fieldName} className="orderable-field-item">
                                                                    <div className="field-order-buttons">
                                                                        <button
                                                                            type="button"
                                                                            className="field-order-btn"
                                                                            onClick={() => {
                                                                                if (index > 0) {
                                                                                    const newFields = [...suggestedFields];
                                                                                    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
                                                                                    setSuggestedFields(newFields);
                                                                                }
                                                                            }}
                                                                            disabled={index === 0}
                                                                            title="Move up"
                                                                        >
                                                                            ↑
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="field-order-btn"
                                                                            onClick={() => {
                                                                                if (index < suggestedFields.length - 1) {
                                                                                    const newFields = [...suggestedFields];
                                                                                    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                                                                                    setSuggestedFields(newFields);
                                                                                }
                                                                            }}
                                                                            disabled={index === suggestedFields.length - 1}
                                                                            title="Move down"
                                                                        >
                                                                            ↓
                                                                        </button>
                                                                    </div>
                                                                    <span className="field-order-name">
                                                                        {fieldName} {metadataType && `(${metadataType.field_type})`}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-actions">
                                        <button onClick={handleSaveSourceType}>
                                            {editingSourceType ? 'Update' : 'Add'}
                                        </button>
                                        <button onClick={() => {
                                            setShowSourceTypeForm(false);
                                            setEditingSourceType(null);
                                            setSourceTypeType('');
                                            setCompulsoryFields([]);
                                            setSuggestedFields([]);
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="items-list">
                            {sourceTypes.length === 0 ? (
                                <p className="no-items">No source types found. Add your first source type!</p>
                            ) : (
                                sourceTypes.map(sourceType => (
                                    <div key={sourceType.id} className="item-card">
                                        <div className="item-info">
                                            <h4>{sourceType.source_type}</h4>
                                            <div className="metadata-info">
                                                <p><strong>Compulsory:</strong> {sourceType.compulsory.join(', ') || 'None'}</p>
                                                <p><strong>Suggested:</strong> {sourceType.suggested.join(', ') || 'None'}</p>
                                            </div>
                                        </div>
                                        <div className="item-actions">
                                            <button onClick={() => handleEditSourceType(sourceType)}>
                                                Edit
                                            </button>
                                            <button 
                                                className="delete-button"
                                                onClick={() => handleDeleteSourceType(sourceType)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                    </div>
                )}

                {/* Metadata Types Tab */}
                {activeTab === 'metadataTypes' && (
                    <div className="metadata-types-section">
                        <div className="section-header">
                            <h2>Metadata Types</h2>
                            <button className="add-button" onClick={handleAddMetadataType}>
                                Add Metadata Type
                            </button>
                        </div>

                        {showMetadataTypeForm && (
                            <div className="form-overlay">
                                <div className="form-modal">
                                    <h3>{editingMetadataType ? 'Edit Metadata Type' : 'Add Metadata Type'}</h3>
                                    <div className="form-group">
                                        <label>Field Name:</label>
                                        <input
                                            type="text"
                                            value={metadataTypeFieldName}
                                            onChange={(e) => setMetadataTypeFieldName(e.target.value)}
                                            placeholder="Enter field name (e.g., author, publisher, isbn)"
                                            disabled={!!editingMetadataType}
                                        />
                                        <small className="form-help-text">
                                            {editingMetadataType ? 'Field name cannot be changed after creation' : 'Must be unique'}
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label>Field Type:</label>
                                        <select
                                            value={metadataTypeFieldType}
                                            onChange={(e) => setMetadataTypeFieldType(e.target.value as MetadataType['field_type'])}
                                        >
                                            <option value="text">Text</option>
                                            <option value="number">Number</option>
                                            <option value="date">Date</option>
                                            <option value="enum">Enum</option>
                                            <option value="select">Select</option>
                                        </select>
                                    </div>
                                    {(metadataTypeFieldName === 'process_metadata_editor_checklist' || metadataTypeFieldName === 'process_metadata_review_checklist') ? (
                                        <div className="form-group">
                                            <label>
                                                {metadataTypeFieldName === 'process_metadata_editor_checklist' 
                                                    ? 'Editor Checklist Items' 
                                                    : 'Review Checklist Items'}
                                            </label>
                                            <div className="checklist-editor">
                                                {checklistItems.map((item, index) => (
                                                    <div key={index} className="checklist-item-editor">
                                                        {metadataTypeFieldName === 'process_metadata_editor_checklist' ? (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    value={item.text || ''}
                                                                    onChange={(e) => {
                                                                        const updated = [...checklistItems];
                                                                        updated[index] = { ...item, text: e.target.value };
                                                                        setChecklistItems(updated);
                                                                    }}
                                                                    placeholder="Checklist item text"
                                                                    style={{ flex: 1, marginRight: '8px' }}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    value={item.keyword || ''}
                                                                    onChange={(e) => {
                                                                        const updated = [...checklistItems];
                                                                        updated[index] = { ...item, keyword: e.target.value };
                                                                        setChecklistItems(updated);
                                                                    }}
                                                                    placeholder="Keyword"
                                                                    style={{ flex: 1, marginRight: '8px', margin: '10px 0px' }}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={item.action || ''}
                                                                    onChange={(e) => {
                                                                        const updated = [...checklistItems];
                                                                        updated[index] = { ...item, action: e.target.value };
                                                                        setChecklistItems(updated);
                                                                    }}
                                                                    placeholder="Action"
                                                                    style={{ flex: 1, marginRight: '8px' }}
                                                                />
                                                            </>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updated = [...checklistItems];
                                                                updated.splice(index, 1);
                                                                setChecklistItems(updated);
                                                            }}
                                                            className="delete-button"
                                                            style={{ padding: '4px 8px', margin: '5px 5px' }}
                                                        >
                                                            Remove
                                                        </button>
                                                        {index > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = [...checklistItems];
                                                                    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
                                                                    setChecklistItems(updated);
                                                                }}
                                                                style={{ padding: '4px 8px', marginLeft: '4px', margin: '5px 5px' }}
                                                            >
                                                                ↑
                                                            </button>
                                                        )}
                                                        {index < checklistItems.length - 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = [...checklistItems];
                                                                    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
                                                                    setChecklistItems(updated);
                                                                }}
                                                                style={{ padding: '4px 8px', marginLeft: '4px', margin: '5px 5px' }}
                                                            >
                                                                ↓
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newItem = metadataTypeFieldName === 'process_metadata_editor_checklist'
                                                            ? { id: `item-${Date.now()}`, text: '', checked: false }
                                                            : { id: `item-${Date.now()}`, keyword: '', action: '', checked: false };
                                                        setChecklistItems([...checklistItems, newItem]);
                                                    }}
                                                    className="add-button"
                                                    style={{ marginTop: '8px' }}
                                                >
                                                    Add Item
                                                </button>
                                            </div>
                                        </div>
                                    ) : (metadataTypeFieldType === 'enum' || metadataTypeFieldType === 'select') && (
                                        <div className="form-group">
                                            <label>Enum/Select Options (comma-separated):</label>
                                            <input
                                                type="text"
                                                value={metadataTypeEnumOptions}
                                                onChange={(e) => setMetadataTypeEnumOptions(e.target.value)}
                                                placeholder="e.g., Option 1, Option 2, Option 3"
                                            />
                                            <small className="form-help-text">
                                                Enter options separated by commas
                                            </small>
                                        </div>
                                    )}
                                    <div className="form-actions">
                                        <button onClick={handleSaveMetadataType} disabled={!metadataTypeFieldName.trim()}>
                                            {editingMetadataType ? 'Update' : 'Add'}
                                        </button>
                                        <button onClick={() => {
                                            setShowMetadataTypeForm(false);
                                            setEditingMetadataType(null);
                                            setMetadataTypeFieldName('');
                                            setMetadataTypeFieldType('text');
                                            setMetadataTypeEnumOptions('');
                                            setChecklistItems([]);
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="items-list">
                            {metadataTypes.length === 0 ? (
                                <p className="no-items">No metadata types found. Add your first metadata type!</p>
                            ) : (
                                metadataTypes.map(metadataType => {
                                    // Check if this is a checklist entry
                                    const isChecklist = metadataType.field_name === 'process_metadata_editor_checklist' || 
                                                       metadataType.field_name === 'process_metadata_review_checklist';
                                    
                                    // Format options display
                                    let optionsDisplay = null;
                                    if (metadataType.enum_options && metadataType.enum_options.length > 0) {
                                        if (isChecklist) {
                                            // For checklist types, show count and item previews
                                            const items = metadataType.enum_options as Array<{id: string, text?: string, keyword?: string, action?: string, checked: boolean}>;
                                            const previewItems = items.slice(0, 3).map(item => {
                                                if (metadataType.field_name === 'process_metadata_editor_checklist') {
                                                    return item.text || 'Untitled';
                                                } else {
                                                    return `${item.keyword || 'Untitled'}${item.action ? ` (${item.action})` : ''}`;
                                                }
                                            });
                                            const remainingCount = items.length - 3;
                                            optionsDisplay = (
                                                <p><strong>Checklist Items ({items.length}):</strong> {previewItems.join(', ')}{remainingCount > 0 ? `, +${remainingCount} more` : ''}</p>
                                            );
                                        } else {
                                            // For regular enum/select types, show string options
                                            const stringOptions = metadataType.enum_options.filter((opt): opt is string => typeof opt === 'string');
                                            if (stringOptions.length > 0) {
                                                optionsDisplay = <p><strong>Options:</strong> {stringOptions.join(', ')}</p>;
                                            }
                                        }
                                    }
                                    
                                    return (
                                        <div key={metadataType.id} className="item-card">
                                            <div className="item-info">
                                                <h4>{metadataType.field_name}</h4>
                                                <div className="metadata-info">
                                                    <p><strong>Type:</strong> {metadataType.field_type}</p>
                                                    {optionsDisplay}
                                                </div>
                                            </div>
                                            <div className="item-actions">
                                                <button onClick={() => handleEditMetadataType(metadataType)}>
                                                    Edit
                                                </button>
                                                <button 
                                                    className="delete-button"
                                                    onClick={() => handleDeleteMetadataType(metadataType)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={showConfirmDialog}
                title="Confirm Action"
                message={confirmMessage}
                onConfirm={() => {
                    if (confirmAction) {
                        confirmAction();
                    }
                }}
                onCancel={() => setShowConfirmDialog(false)}
                isDestructive={true}
            />
        </div>
    );
};

export default Admin;
